import { Box, Stack, Typography, useTheme } from "@mui/material";
import NoDataImage from "assets/images/noData.svg";
import { Button, CircularLoader, DeleteConfirmationModal } from "components";
import {
  useDeleteBillingTemplate,
  useFetchBillingTemplateDetail,
  useGetBillingTemplates,
} from "controller";
import { useState } from "react";
import toast from "react-hot-toast";
import type { BillingTemplateListItem, BillingTemplateType } from "shared";
import { getApiErrorMessage, useInfiniteScroll } from "utilz/helpers";
import { BillingTemplateCard } from "./BillingTemplateCard";

const CARD_MIN_WIDTH = "312px";

// mapping_id comes from two separate tables, so pair it with type.
const toTemplateKey = (template: BillingTemplateListItem) =>
  `${template.type}::${template.mapping_id}`;

interface BillingTemplateProps {
  /** Receives the FULL template row, safe to seed the edit drawer with. */
  onEdit: (template: BillingTemplateListItem) => void;
  searchQuery?: string;
  /** Empty = both types. */
  typeFilter?: BillingTemplateType[];
  /** Shown as "Clear filters" on the no-match state; omit when not filterable. */
  onClearFilters?: () => void;
}

const NO_TYPE_FILTER: BillingTemplateType[] = [];

/** General and customer-specific templates as one card grid. */
function BillingTemplate({
  onEdit,
  searchQuery = "",
  typeFilter = NO_TYPE_FILTER,
  onClearFilters,
}: BillingTemplateProps) {
  const theme = useTheme();
  const [templateToDelete, setTemplateToDelete] =
    useState<BillingTemplateListItem | null>(null);

  /** The card whose full row is loading, so a second click can't race it. */
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const { mutateAsync: deleteBillingTemplate, isLoading: isDeleting } =
    useDeleteBillingTemplate();
  const fetchTemplateDetail = useFetchBillingTemplateDetail();
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetBillingTemplates({ types: typeFilter, searchQuery });
  const templates = data?.pages.flatMap((page) => page.result) ?? [];

  const { rootRef, sentinelRef } = useInfiniteScroll({
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  const isFiltered = searchQuery.trim() !== "" || typeFilter.length > 0;

  // List rows are lightweight; the drawer must get the full row or Save
  // blanks the taxes, discounts and emails the list doesn't carry.
  const openTemplate = async (template: BillingTemplateListItem) => {
    if (openingKey) return;
    setOpeningKey(toTemplateKey(template));
    try {
      onEdit(await fetchTemplateDetail(template));
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          "Unable to open the template. Please try again",
        ),
      );
    } finally {
      setOpeningKey(null);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    try {
      await deleteBillingTemplate({
        invoice_template_ids: [templateToDelete.mapping_id],
        type: templateToDelete.type,
      });
      setTemplateToDelete(null);
      toast.success("Template successfully deleted");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Please try again later"));
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <CircularLoader />
        </Stack>
      );
    }
    if (isError) {
      return (
        <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Couldn't load billing templates. Please try again.
          </Typography>
        </Stack>
      );
    }
    if (templates.length === 0) {
      return (
        <Stack
          sx={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          <img src={NoDataImage} alt="No billing templates" width="200px" />
          <Typography variant="body-large">
            {isFiltered
              ? "No templates match your filters"
              : "No billing templates added"}
          </Typography>
          {isFiltered && onClearFilters && (
            <Button variant="secondary" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}
        </Stack>
      );
    }
    return (
      <Box
        sx={{
          display: "grid",
          // Figma card is 312px wide. Fit as many as the content area allows —
          // MUI Grid breakpoints follow the window, not this panel.
          gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}, 1fr))`,
          gap: "16px",
          p: "24px",
        }}
      >
        {templates.map((template) => (
          <BillingTemplateCard
            key={toTemplateKey(template)}
            template={template}
            isOpening={openingKey === toTemplateKey(template)}
            onClick={() => openTemplate(template)}
            onDelete={() => setTemplateToDelete(template)}
          />
        ))}
        {isFetchingNextPage && (
          <Stack sx={{ gridColumn: "1 / -1", alignItems: "center" }}>
            <CircularLoader size="24px" />
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        bgcolor: theme.palette.darkBg.high,
      }}
    >
      {renderContent()}
      {/* Scrolling this into view loads the next page. */}
      <Box ref={sentinelRef} sx={{ height: "1px", flexShrink: 0 }} />
      {templateToDelete && (
        <DeleteConfirmationModal
          open={!!templateToDelete}
          keepTitleOriginalCasing={true}
          isLoading={isDeleting}
          onPrimaryButtonClick={handleDelete}
          onSecondaryButtonClick={() => setTemplateToDelete(null)}
          title="Delete billing template"
          tagline="Are you sure you want to delete?"
        />
      )}
    </Box>
  );
}

export default BillingTemplate;
